import type { CodiceContatore } from '@praticheenergia/domain'
import { arrotonda, dec, somma, type Dec } from './dec'
import { mesiDelPeriodo, type ContatorePeriodo, type DatiPeriodo } from './ingresso'
import type { RegolePeriodo } from './regole-periodo'

export interface ConsumoMensile {
  readonly contatoreId: string
  readonly codice: CodiceContatore
  readonly matricola: string
  /** Assente quando le regole chiedono un solo consumo per il periodo (annuale). */
  readonly mese?: number | undefined
  readonly lettA?: Dec | undefined
  readonly lettP?: Dec | undefined
  readonly diffLett?: Dec | undefined
  readonly costLett?: Dec | undefined
  readonly kwh: Dec
}

/**
 * Un mese senza lettura (o senza la lettura precedente) non produce consumo:
 * `validaPeriodo` lo segnala come bloccante prima che si arrivi qui.
 */
function consumiDelContatore(dati: DatiPeriodo, c: ContatorePeriodo, energia: number): ConsumoMensile[] {
  const { precedente, mesi } = mesiDelPeriodo(dati.periodo, dati.annoImposta)
  const valoreDi = (anno: number, mese: number): Dec | undefined =>
    dati.letture.find((l) => l.contatoreId === c.id && l.anno === anno && l.mese === mese)?.valore
  const base = { contatoreId: c.id, codice: c.codice, matricola: c.matricola }

  const out: ConsumoMensile[] = []
  let prec = valoreDi(precedente.anno, precedente.mese)
  for (const { anno, mese } of mesi) {
    const v = valoreDi(anno, mese)
    if (c.kwhDiretti) {
      if (v !== undefined) out.push({ ...base, mese, kwh: arrotonda(v, energia) })
      continue
    }
    if (v !== undefined && prec !== undefined) {
      const giroDiQuadrante = v.lt(prec) && c.cifreIntere > 0
      const diffLett = giroDiQuadrante ? dec(10).pow(c.cifreIntere).minus(prec).plus(v) : v.minus(prec)
      out.push({
        ...base,
        mese,
        lettA: v,
        lettP: prec,
        diffLett,
        costLett: c.costanteLettura,
        kwh: arrotonda(diffLett.times(c.costanteLettura), energia),
      })
    }
    prec = v
  }
  return out
}

/**
 * Un solo consumo di periodo: prima lettura precedente, ultima attuale, somme dei mesi.
 *
 * I kWh si ricavano dalla differenza totale, NON sommando i kWh mensili: sommare
 * dodici valori gia' arrotondati romperebbe l'identita' `kWh = DiffLett x CostLett`
 * sulla riga unica del periodo (con costante 1,5 e differenza 10,5 al mese darebbe
 * 192 invece di 189). Sulla riga aggregata l'arrotondamento mensile non ha
 * giustificazione fiscale: c'e' un solo arrotondamento, quello finale.
 */
function consumoDelPeriodo(mensili: readonly ConsumoMensile[], energia: number): ConsumoMensile[] {
  const primo = mensili[0]
  const ultimo = mensili[mensili.length - 1]
  if (primo === undefined || ultimo === undefined) return []
  const diffLett = primo.diffLett === undefined ? undefined : somma(...mensili.map((m) => m.diffLett ?? dec(0)))
  return [
    {
      contatoreId: primo.contatoreId,
      codice: primo.codice,
      matricola: primo.matricola,
      lettP: primo.lettP,
      lettA: ultimo.lettA,
      costLett: primo.costLett,
      diffLett,
      kwh:
        diffLett === undefined || primo.costLett === undefined
          ? somma(...mensili.map((m) => m.kwh))
          : arrotonda(diffLett.times(primo.costLett), energia),
    },
  ]
}

export function consumiMensili(dati: DatiPeriodo, regole: RegolePeriodo): ConsumoMensile[] {
  return dati.contatori
    .filter((c) => c.codice !== 'altri')
    .flatMap((c) => {
      const mensili = consumiDelContatore(dati, c, regole.arrotondamento.energia)
      return regole.granularita === 'periodo'
        ? consumoDelPeriodo(mensili, regole.arrotondamento.energia)
        : mensili
    })
}
